import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { EmailIntegrationsContext } from "@redotech/redo-merchant-support-app/services/email-integrations";
import {
  ContactChannel,
  type ReceivedAtCondition as ReceivedAtConditionType,
} from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useMemo, useState } from "react";
import * as styles from "./conditions.module.css";

const channelOptions = [
  {
    id: ContactChannel.EMAIL,
    type: "text" as const,
    text: "Email",
    value: ContactChannel.EMAIL,
  },
];

export const ReceivedAtCondition = memo(function ReceivedAtCondition({
  condition,
  onChange,
}: {
  condition: ReceivedAtConditionType;
  onChange: (updated: ReceivedAtConditionType) => void;
}) {
  const [availableEmails, setAvailableEmails] = useState<string[]>([]);
  const [emailIntegrationsLoad] = useLazyContext(EmailIntegrationsContext);
  const team = useRequiredContext(TeamContext);

  const selectedChannel = useMemo(
    () =>
      channelOptions.find(
        (opt) => opt.value === (condition.channel ?? ContactChannel.EMAIL),
      ),
    [condition.channel],
  );

  useEffect(() => {
    if (!emailIntegrationsLoad.value) {
      return;
    }

    const emails = [
      ...(emailIntegrationsLoad.value.customIntegrations || []),
      ...(emailIntegrationsLoad.value.gmailIntegrations || []),
      ...(emailIntegrationsLoad.value.outlookIntegrations || []),
    ].map((integration) => integration.email);

    const defaultEmail = team.settings?.support?.defaultEmail;
    if (defaultEmail && !emails.includes(defaultEmail)) {
      emails.push(defaultEmail);
    }

    setAvailableEmails((oldEmails) => {
      const updatedEmails = [...oldEmails];
      for (const email of emails) {
        if (!updatedEmails.includes(email)) {
          updatedEmails.push(email);
        }
      }
      return updatedEmails;
    });
  }, [emailIntegrationsLoad.value, team]);

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const emailItems: RedoListItem<string>[] = useMemo(() => {
    const emails = availableEmails.length
      ? availableEmails
      : ["No email addresses configured"];
    return emails.map((email) => ({
      id: email,
      type: "text" as const,
      text: email,
      value: email,
    }));
  }, [availableEmails]);

  const selectedItems = useMemo(() => {
    return (condition.values || []).map((v) => ({
      id: v,
      type: "text" as const,
      text: v,
      value: v,
    }));
  }, [condition.values]);

  const displayText =
    (condition.values?.length || 0) > 0
      ? condition.values!.length === 1
        ? condition.values![0]
        : `${condition.values!.length} emails selected`
      : "Select email addresses";

  const isDisabled = availableEmails.length === 0;

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput
        options={channelOptions}
        optionSelected={({ item }) =>
          onChange({ ...condition, channel: item.value, values: [] })
        }
        placeholder="Select channel"
        selectedItem={selectedChannel}
      />
      {(condition.channel ?? ContactChannel.EMAIL) === ContactChannel.EMAIL && (
        <Flex dir="column" gap="sm" w="full">
          <RedoFilterDropdownAnchor
            className={styles.fullWidthButton}
            color={isDisabled ? "tertiary" : "primary"}
            onClick={() => {
              if (!isDisabled) {
                setDropdownOpen(!dropdownOpen);
              }
            }}
            ref={setDropdownButtonRef}
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            text={displayText}
          />
          <RedoMultiselectDropdown
            dropdownAnchor={dropdownButtonRef}
            dropdownOpen={dropdownOpen}
            fitToAnchor
            items={emailItems}
            selectedItems={selectedItems}
            selectionVariant="checkbox"
            setDropdownOpen={setDropdownOpen}
            setSelectedItems={(items) => {
              onChange({
                ...condition,
                values: items.map((item) => item.value),
              });
            }}
            size="xs"
          />
        </Flex>
      )}
    </Flex>
  );
});
